/**
 * Image URL Transformation Service
 *
 * Converts S3 URLs to CloudFront CDN URLs at render time for better performance.
 * This approach is safe - no database changes, instant rollback by disabling.
 */

// Environment-aware CDN domains
const CDN_DOMAINS: Record<string, string> = {
  prod: 'image.coffeemasterpro.com',
  staging: 'image.staging.coffeemasterpro.com',
  dev: 'image.dev.coffeemasterpro.com',
};

// S3 bucket patterns to match (all URL formats)
// Handles both with region (s3.us-east-2.amazonaws.com) and without (s3.amazonaws.com)
const S3_PATTERNS = [
  /^https?:\/\/cafemaster-media-(dev|staging|prod)\.s3(?:\.[\w-]+)?\.amazonaws\.com\//,
  /^https?:\/\/s3(?:\.[\w-]+)?\.amazonaws\.com\/cafemaster-media-(dev|staging|prod)\//,
];

// Build-time environment from Vite (statically replaced at build)
// @ts-ignore - Vite replaces this at build time
const BUILD_STAGE: string = import.meta.env.VITE_STAGE || '';

/**
 * Detect current environment
 * Priority: VITE_STAGE (build-time) > hostname detection > default to dev
 */
const getEnvironment = (): 'prod' | 'staging' | 'dev' => {
  // Check build-time VITE_STAGE first (for native apps)
  if (BUILD_STAGE === 'prod') return 'prod';
  if (BUILD_STAGE === 'staging') return 'staging';
  if (BUILD_STAGE === 'dev') return 'dev';

  // Fall back to hostname detection (for browser access)
  if (typeof window === 'undefined') return 'dev';
  const hostname = window.location.hostname;
  if (hostname === 'cafemenumaster.com' || hostname === 'www.cafemenumaster.com') return 'prod';
  if (hostname.includes('staging')) return 'staging';
  return 'dev';
};

/**
 * Transform S3 URL to CloudFront CDN URL
 *
 * @param url - The image URL (S3, CloudFront, or any other)
 * @returns CloudFront URL if input was S3, otherwise returns input unchanged
 *
 * @example
 * // S3 URL → CloudFront URL
 * toCloudFrontUrl('https://cafemaster-media-prod.s3.us-east-2.amazonaws.com/tenant/img.jpg')
 * // Returns: 'https://image.coffeemasterpro.com/tenant/img.jpg'
 *
 * @example
 * // Already CloudFront → unchanged
 * toCloudFrontUrl('https://image.coffeemasterpro.com/tenant/img.jpg')
 * // Returns: 'https://image.coffeemasterpro.com/tenant/img.jpg'
 *
 * @example
 * // External URL → unchanged
 * toCloudFrontUrl('https://example.com/image.jpg')
 * // Returns: 'https://example.com/image.jpg'
 */
export const toCloudFrontUrl = (url: string | undefined | null): string => {
  if (!url) return '';

  // Already a CloudFront URL - return as-is
  if (url.includes('image.coffeemasterpro.com') ||
      url.includes('image.dev.coffeemasterpro.com') ||
      url.includes('image.staging.coffeemasterpro.com')) {
    return url;
  }

  // Check if it's an S3 URL we can transform
  for (const pattern of S3_PATTERNS) {
    if (pattern.test(url)) {
      const env = getEnvironment();
      const cdnDomain = CDN_DOMAINS[env];
      // Extract the path after the bucket
      const path = url.replace(pattern, '');
      return `https://${cdnDomain}/${path}`;
    }
  }

  // Not an S3 URL - return as-is (could be external URL, data URL, placeholder, etc.)
  return url;
};

/**
 * React hook for transformed image URL (for consistency with hook patterns)
 */
export const useCloudFrontImage = (url: string | undefined | null): string => {
  return toCloudFrontUrl(url);
};
